import React from 'react';
import theme from '../theme.config';

/**
 * HeroVisual — VoltaGex Old Java Temple Era
 * Active variant: 'volta-mandala' — animated sacred mandala / stupa nexus
 */
const VARIANT = (theme.style && theme.style.heroVisual) || 'volta-mandala';

// ── VoltaGex: Rotating Sacred Mandala + Stupa Nexus ──────────────────────
function VoltaMandala() {
  const orbParticles = Array.from({ length: 8 });
  return (
    <div className="relative w-72 h-72 flex items-center justify-center">
      {/* Outer ambient glow */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: 'radial-gradient(circle, hsl(var(--primary) / 0.12) 0%, transparent 70%)',
          filter: 'blur(20px)',
        }}
      />

      {/* Ring 1 — outermost, slow mandala pattern */}
      <div
        className="absolute w-72 h-72 rounded-full animate-mandala-slow"
        style={{
          border: '1px dashed hsl(var(--primary) / 0.3)',
          boxShadow: '0 0 8px hsl(var(--primary) / 0.08)',
        }}
      >
        {/* Lotus petals on ring 1 */}
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              left: '50%',
              top: '50%',
              width: '8px',
              height: '8px',
              marginLeft: '-4px',
              marginTop: '-4px',
              transform: `rotate(${deg}deg) translateY(-134px)`,
            }}
          >
            <div
              className="w-2 h-2 rounded-full"
              style={{ background: 'hsl(var(--primary) / 0.5)', boxShadow: '0 0 4px hsl(var(--primary) / 0.4)' }}
            />
          </div>
        ))}
      </div>

      {/* Ring 2 — counter-rotating geometric pattern */}
      <div
        className="absolute w-56 h-56 rounded-full animate-mandala-rev"
        style={{
          border: '1.5px solid hsl(var(--primary) / 0.4)',
        }}
      >
        {/* Diamond markers on ring 2 */}
        {[0, 60, 120, 180, 240, 300].map((deg, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              left: '50%',
              top: '50%',
              width: '6px',
              height: '6px',
              marginLeft: '-3px',
              marginTop: '-3px',
              transform: `rotate(${deg}deg) translateY(-104px) rotate(-${deg}deg)`,
            }}
          >
            <div
              style={{
                width: '6px',
                height: '6px',
                background: 'hsl(var(--secondary) / 0.7)',
                transform: 'rotate(45deg)',
                boxShadow: '0 0 6px hsl(var(--secondary) / 0.5)',
              }}
            />
          </div>
        ))}
      </div>

      {/* Ring 3 — inner faster mandala */}
      <div
        className="absolute w-40 h-40 rounded-full animate-mandala"
        style={{
          border: '1px solid hsl(var(--primary) / 0.5)',
          borderStyle: 'solid',
        }}
      >
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 160 160">
          {/* 4 lotus arcs */}
          {[0, 90, 180, 270].map((deg, i) => (
            <g key={i} transform={`rotate(${deg} 80 80)`}>
              <path
                d="M80,8 Q95,30 80,45 Q65,30 80,8"
                fill="none"
                stroke="hsl(42deg 69% 47% / 0.4)"
                strokeWidth="1"
              />
            </g>
          ))}
          {/* 4 diagonal lotus arcs */}
          {[45, 135, 225, 315].map((deg, i) => (
            <g key={i} transform={`rotate(${deg} 80 80)`}>
              <path
                d="M80,8 Q95,30 80,45 Q65,30 80,8"
                fill="none"
                stroke="hsl(18deg 73% 37% / 0.3)"
                strokeWidth="0.8"
              />
            </g>
          ))}
        </svg>
      </div>

      {/* Orbiting offering particles */}
      {orbParticles.map((_, i) => {
        const delay = i * 1.25;
        const isGold = i % 3 !== 2;
        return (
          <div
            key={i}
            className="absolute"
            style={{
              left: '50%',
              top: '50%',
              width: '5px',
              height: '5px',
              marginLeft: '-2.5px',
              marginTop: '-2.5px',
              animation: `orbit ${10 + i * 1.5}s linear ${delay}s infinite`,
              animationDelay: `${-delay}s`,
            }}
          >
            <div
              className="w-1.5 h-1.5 rounded-full animate-gold-pulse"
              style={{
                background: isGold ? 'hsl(var(--primary))' : 'hsl(var(--secondary))',
                boxShadow: isGold
                  ? '0 0 6px hsl(var(--primary)), 0 0 12px hsl(var(--primary) / 0.5)'
                  : '0 0 6px hsl(var(--secondary)), 0 0 12px hsl(var(--secondary) / 0.5)',
                animationDelay: `${i * 0.4}s`,
              }}
            />
          </div>
        );
      })}

      {/* Central stupa silhouette */}
      <div className="relative z-10 flex items-center justify-center animate-float">
        <svg
          width="64"
          height="78"
          viewBox="0 0 64 78"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ filter: 'drop-shadow(0 0 12px hsl(42deg 69% 47% / 0.6)) drop-shadow(0 0 30px hsl(42deg 69% 47% / 0.3))' }}
        >
          <defs>
            <linearGradient id="stupaGold" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#F0D060" />
              <stop offset="50%" stopColor="#C9A227" />
              <stop offset="100%" stopColor="#8B6914" />
            </linearGradient>
          </defs>
          {/* Base */}
          <rect x="2" y="68" width="60" height="8" rx="1" fill="url(#stupaGold)" opacity="0.9" />
          {/* Tier 1 */}
          <rect x="8" y="56" width="48" height="13" rx="1" fill="url(#stupaGold)" opacity="0.85" />
          {/* Tier 2 */}
          <rect x="15" y="44" width="34" height="13" rx="1" fill="url(#stupaGold)" opacity="0.9" />
          {/* Tier 3 */}
          <rect x="21" y="32" width="22" height="13" rx="1" fill="url(#stupaGold)" opacity="0.92" />
          {/* Bell */}
          <rect x="25" y="20" width="14" height="13" rx="2" fill="url(#stupaGold)" opacity="0.95" />
          {/* Harmika */}
          <rect x="27" y="12" width="10" height="9" rx="1" fill="url(#stupaGold)" />
          {/* Spire base */}
          <rect x="29" y="6" width="6" height="7" rx="1" fill="url(#stupaGold)" />
          {/* Spire tip */}
          <polygon points="32,1 29,6 35,6" fill="url(#stupaGold)" />
          {/* V carved into stupa */}
          <line x1="24" y1="35" x2="29" y2="48" stroke="#0D0906" strokeWidth="2" strokeLinecap="round" />
          <line x1="40" y1="35" x2="35" y2="48" stroke="#0D0906" strokeWidth="2" strokeLinecap="round" />
          <line x1="29" y1="48" x2="32" y2="53" stroke="#0D0906" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="32" y1="53" x2="35" y2="48" stroke="#0D0906" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>

      {/* HUD labels */}
      <div
        className="absolute bottom-3 left-0 right-0 text-center animate-torch"
        style={{ fontFamily: "'Space Mono', monospace", fontSize: '8px', letterSpacing: '0.3em', color: 'hsl(var(--primary) / 0.7)' }}
      >
        ✦ NEXUS AKTIF ✦
      </div>
      <div
        className="absolute top-4 left-5"
        style={{ fontFamily: "'Space Mono', monospace", fontSize: '7px', letterSpacing: '0.15em', color: 'hsl(var(--muted-foreground))' }}
      >
        STUPA · VGX
      </div>
      <div
        className="absolute top-4 right-5"
        style={{ fontFamily: "'Space Mono', monospace", fontSize: '7px', color: 'hsl(var(--secondary) / 0.6)' }}
      >
        ● AKTIF
      </div>
    </div>
  );
}

// ── Legacy variants preserved (not used but kept) ─────────────────────────
function ControllerOrb() {
  return (
    <div className="relative w-64 h-64">
      <div className="absolute inset-0 rounded-full bg-primary/20 blur-3xl animate-pulse" />
      <div className="absolute inset-6 rounded-full border-2 border-primary/40 animate-spin-slow" />
      <div className="absolute inset-24 rounded-full bg-gradient-to-br from-primary to-accent shadow-2xl" />
    </div>
  );
}

const VARIANTS = {
  'volta-mandala': VoltaMandala,
  'controller-orb': ControllerOrb,
};

export default function HeroVisual({ className = '', compact = false }) {
  const Component = VARIANTS[VARIANT] || VoltaMandala;
  return (
    <div
      data-testid="hero-visual"
      className={`relative ${compact ? 'scale-75' : ''} ${className}`}
    >
      <Component />
    </div>
  );
}
